import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';

import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import {
  selectWorkspaceDetails,
  selectWorkspaceDetailsLoading,
  selectUsers
} from 'store/Workspace/selectors';

import { fetchWorkspaceDetails, getUsers } from 'Screens/Contextualization/Workspaces/actions/Actions';
import SpinnerComponent from 'Common/Components/SpinnerContainer';
import Importance from 'Common/Components/Importance';

import {
  Wrapper,
  Header,
  Title,
  Chevron,
  Content,
  Grid,
  Col,
  Row,
  Key,
  Value,
  ValuePrimary,
  Divider,
  UsersList,
  UserRow,
  Role,
  RowTop,
  Username
} from './styled';

const formatDate = (value) => {
  if (!value && value !== 0) return '-';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).format(d);
};

const normalizeScope = (scope) => {
  if (!scope) return '-';
  if (typeof scope === 'string') return scope;
  if (!Array.isArray(scope) || scope.length === 0) return '-';
  if (scope.length === 2) return `${scope[0]} - ${scope[1]}`;
  return scope.join(', ');
};

const pickMainRole = (roles) => {
  let arr = [];

  if (Array.isArray(roles)) {
    arr = roles;
  } else if (roles) {
    arr = [roles];
  }

  const priority = ['admin', 'asset_owner', 'pentester', 'client'];
  return priority.find((r) => arr.includes(r)) || arr[0] || null;
};

const roleLabel = (roleKey) => {
  const map = {
    admin: 'Administrator',
    pentester: 'Pentester',
    asset_owner: 'Asset Owner',
    client: 'Client',
    system: 'System',
    user: 'User'
  };
  return map[roleKey] || roleKey;
};

const STORAGE_OPEN_KEY = 'dashboard.workspaceDetails.open';

const WorkspaceDetails = ({ embedded = false, onOpenChange }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_OPEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open, onOpenChange]);

  const wsName = useSelector(selectCurrentWorkspace);
  const details = useSelector(selectWorkspaceDetails);
  const loading = useSelector(selectWorkspaceDetailsLoading);

  const usersList = useSelector(selectUsers);

  useEffect(() => {
    if (!open || !wsName) return;

    dispatch(fetchWorkspaceDetails(wsName));
    if (!usersList || usersList.length === 0) dispatch(getUsers());
  }, [open, wsName, dispatch]);

  const onToggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;

      try {
        localStorage.setItem(STORAGE_OPEN_KEY, next ? '1' : '0');
      } catch (e) { /* ignore */ }

      if (next && wsName) {
        dispatch(fetchWorkspaceDetails(wsName));
        if (!usersList || usersList.length === 0) dispatch(getUsers());
      }

      return next;
    });
  }, [dispatch, wsName, usersList]);

  const onHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  const wsData = useMemo(() => get(details, 'data', details) || {}, [details]);

  const name = get(wsData, 'name', '-');
  const customer = get(wsData, 'customer', '-') || '-';
  const scope = normalizeScope(get(wsData, 'scope', null));
  const importance = get(wsData, 'importance', 0);

  const startDate = formatDate(get(wsData, 'duration.start_date', null));
  const endDate = formatDate(get(wsData, 'duration.end_date', null));

  const assignedUsernames = get(wsData, 'users', []) || [];
  const description = get(wsData, 'description', '');

  const roleByUsername = useMemo(() => {
    const map = new Map();
    (usersList || []).forEach((u) => {
      const username = u?.username || u?.name;
      if (!username) return;
      const main = pickMainRole(u?.roles);
      if (main) map.set(username, main);
    });
    return map;
  }, [usersList]);

  const noop = useCallback(() => { }, []);

  return (
    <Wrapper $embedded={ embedded }>
      <Header
        onClick={ onToggle }
        onKeyDown={ onHeaderKeyDown }
        role="button"
        tabIndex={ 0 }
        aria-expanded={ open }
      >
        <Title>Workspace Details</Title>
        <Chevron $open={ open } />
      </Header>

      {open && (
        <Content $embedded={ embedded }>
          {loading
            ? (
              <SpinnerComponent height="90px" />
              )
            : (
              <>
                <Grid>
                  <Col>
                    <Row>
                      <Key>Name:</Key>
                      <ValuePrimary>{name}</ValuePrimary>
                    </Row>
                    <Row>
                      <Key>Customer:</Key>
                      <Value>{customer}</Value>
                    </Row>
                    <Row>
                      <Key>Scope:</Key>
                      <Value>{scope}</Value>
                    </Row>
                  </Col>

                  <Col>
                    <Row>
                      <Key>Importance:</Key>
                      <Value>
                        <Importance importance={ importance } callback={ noop } disabled />
                      </Value>
                    </Row>
                    <Row>
                      <Key>Start Date:</Key>
                      <Value>{startDate}</Value>
                    </Row>
                    <Row>
                      <Key>End Date:</Key>
                      <Value>{endDate}</Value>
                    </Row>
                  </Col>
                </Grid>

                <Divider />

                <Grid>
                  <Col>
                    <RowTop>
                      <Key>Assigned Users:</Key>

                      <UsersList>
                        {Array.isArray(assignedUsernames) && assignedUsernames.length > 0
                          ? (
                              assignedUsernames.map((username, idx) => {
                                const roleKey = roleByUsername.get(username) || 'user';
                                return (
                                <UserRow key={ `${username}-${idx}` }>
                                  <Username title={ username }>{username}</Username>
                                  <Role $role={ roleKey }>{roleLabel(roleKey)}</Role>
                                </UserRow>
                                );
                              })
                            )
                          : (
                            <Value>-</Value>
                            )}
                      </UsersList>
                    </RowTop>
                  </Col>

                  <Col>
                    <Row>
                      <Key>Description:</Key>
                    </Row>
                    <Value title={ description }>{description || '-'}</Value>
                  </Col>
                </Grid>
              </>
              )}
        </Content>
      )}
    </Wrapper>
  );
};

export default WorkspaceDetails;
